import axios from 'axios';

// Base currency configuration
export const BASE_CURRENCY = 'INR';
export const BASE_CURRENCY_SYMBOL = '₹';

// Currency definitions with base conversion rates (fallback)
export const CURRENCIES = {
  INR: { code: 'INR', symbol: '₹', name: 'Indian Rupee', conversionRate: 1 },
  USD: { code: 'USD', symbol: '$', name: 'US Dollar', conversionRate: 83.5 },
  EUR: { code: 'EUR', symbol: '€', name: 'Euro', conversionRate: 89.2 },
  GBP: { code: 'GBP', symbol: '£', name: 'British Pound', conversionRate: 105.8 },
  JPY: { code: 'JPY', symbol: '¥', name: 'Japanese Yen', conversionRate: 0.56 },
  AUD: { code: 'AUD', symbol: 'A$', name: 'Australian Dollar', conversionRate: 54.8 },
  CAD: { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar', conversionRate: 61.2 },
  SGD: { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar', conversionRate: 62.1 },
  AED: { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham', conversionRate: 22.7 }
};

// Credit packages with base INR prices
export const CREDIT_PACKAGES = [
  { id: 'basic', credits: 5, inrPrice: 9999, bonus: 0, popular: false },
  { id: 'pro', credits: 15, inrPrice: 25000, bonus: 3, popular: true },
  { id: 'enterprise', credits: 30, inrPrice: 45000, bonus: 10, popular: false }
];

// Function to round up prices according to market standards
export const roundUpPrice = (amount, currency) => {
  let rounded;
  
  switch(currency) {
    case 'INR':
      rounded = Math.ceil(amount); // Round to nearest rupee
      break;
    case 'USD':
    case 'EUR':
    case 'GBP':
    case 'AUD':
    case 'CAD':
    case 'SGD':
      rounded = Math.ceil(amount * 100) / 100; // Round to nearest cent/fils
      break;
    case 'JPY':
      rounded = Math.ceil(amount); // Round to nearest yen
      break;
    case 'AED':
      rounded = Math.ceil(amount * 100) / 100; // Round to nearest fils
      break;
    default:
      rounded = Math.ceil(amount);
  }
  
  // Format to appropriate decimal places
  const decimalPlaces = currency === 'JPY' ? 0 : 2;
  return parseFloat(rounded.toFixed(decimalPlaces));
};


// Convert price from INR to target currency
export const convertPrice = (inrAmount, targetCurrency, conversionRates) => {
  const rate = conversionRates[targetCurrency]?.conversionRate || 1;
  const converted = inrAmount / rate;
  return roundUpPrice(converted, targetCurrency);
};

export const fetchExchangeRates = async () => {
  try {
    // Using ExchangeRate-API (free tier available)
    const response = await axios.get(
      `https://api.exchangerate-api.com/v4/latest/INR`
    );
    return response.data.rates;
  } catch (error) {
    console.error('Failed to fetch exchange rates:', error);
    // Fallback to static rates
    return {
      USD: 0.012,
      EUR: 0.011,
      GBP: 0.0095,
      JPY: 1.79,
      AUD: 0.018,
      CAD: 0.016,
      SGD: 0.016,
      AED: 0.044
    };
  }
};


// Fetch live conversion rates from API
export const fetchLiveRates = async () => {
  try {
    // For production, use a real API like:
    // const response = await axios.get(`https://api.exchangerate-api.com/v4/latest/${BASE_CURRENCY}`);
    // const rates = response.data.rates;
    
    // Mock API response with slightly different rates for realism
    const mockRates = {
      USD: 83.7 + (Math.random() * 2 - 1), // ±1 variation
      EUR: 89.5 + (Math.random() * 2 - 1),
      GBP: 106.1 + (Math.random() * 2 - 1),
      JPY: 0.57 + (Math.random() * 0.02 - 0.01),
      AUD: 55.1 + (Math.random() * 1 - 0.5),
      CAD: 61.5 + (Math.random() * 1 - 0.5),
      SGD: 62.4 + (Math.random() * 1 - 0.5),
      AED: 22.8 + (Math.random() * 0.5 - 0.25),
      INR: 1
    };

    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 800));
    
    return mockRates;
  } catch (error) {
    console.error('Failed to fetch live rates:', error);
    // Return base rates as fallback
    const fallbackRates = {};
    Object.keys(CURRENCIES).forEach(currency => {
      fallbackRates[currency] = CURRENCIES[currency].conversionRate;
    });
    return fallbackRates;
  }
};

// Update your convertPrice function
export const convertPriceWithLiveRates = (inrAmount, targetCurrency, rates) => {
  const rate = rates[targetCurrency] || 1;
  const converted = inrAmount * rate;
  return roundUpPrice(converted, targetCurrency);
};

// Initialize currency rates with live data or fallback
export const initializeCurrencyRates = async () => {
  try {
    const liveRates = await fetchLiveRates();
    const updatedCurrencies = { ...CURRENCIES };
    
    Object.keys(liveRates).forEach(currency => {
      if (updatedCurrencies[currency]) {
        updatedCurrencies[currency].conversionRate = liveRates[currency];
      }
    });
    
    return updatedCurrencies;
  } catch (error) {
    console.error('Failed to initialize currency rates:', error);
    return CURRENCIES;
  }
};

// Format currency for display
export const formatCurrency = (amount, currencyCode, showSymbol = true) => {
  const currency = CURRENCIES[currencyCode];
  if (!currency) return `${amount}`;
  
  const formattedAmount = amount.toLocaleString('en-IN', {
    minimumFractionDigits: currencyCode === 'JPY' ? 0 : 2,
    maximumFractionDigits: currencyCode === 'JPY' ? 0 : 2
  });
  
  return showSymbol ? `${currency.symbol}${formattedAmount}` : formattedAmount;
};

// Calculate price per credit
export const calculatePricePerCredit = (packageData, currencyRates, targetCurrency) => {
  const convertedPrice = convertPrice(packageData.inrPrice, targetCurrency, currencyRates);
  const totalCredits = packageData.credits + packageData.bonus;
  return convertedPrice / totalCredits;
};

// Get user's local currency based on browser or IP
export const detectUserCurrency = () => {
  try {
    // Try to get from browser locale
    const userLocale = navigator.language || 'en-US';
    const region = userLocale.split('-')[1];
    
    const regionToCurrency = {
      US: 'USD',
      GB: 'GBP',
      EU: 'EUR',
      DE: 'EUR',
      FR: 'EUR',
      IT: 'EUR',
      ES: 'EUR',
      AU: 'AUD',
      CA: 'CAD',
      JP: 'JPY',
      SG: 'SGD',
      AE: 'AED',
      IN: 'INR'
    };
        // Force INR as default regardless of detection
    return 'INR'; // Add this line to override all detection

    //return regionToCurrency[region] || BASE_CURRENCY;
  } catch (error) {
	return 'INR'; 
    //return BASE_CURRENCY;
  }
};

// Remove the React hook from this file since it's a utility file
// Currency hooks should be in separate hook files